// src/components/whatsapp/WhatsAppBroadcastsPage.tsx
// Track E5 — WhatsApp broadcasts (backed by mcp_django B3).
//
// List with progress bars + create wizard (template → audience → variables)
// with live audience preview + start/cancel lifecycle actions.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Ban, CheckCircle, ChevronRight, Clock, Eye, Loader2, PlayCircle, Plus,
  RefreshCw, Send, Users, XCircle,
} from 'lucide-react';

import {
  ApiError,
  broadcastProgressText,
  cancelBroadcast, createBroadcast, deleteBroadcast, getBroadcast,
  listBroadcasts, listTemplates, previewBroadcastAudience, startBroadcast,
  type BroadcastStatus, type WhatsAppBroadcast, type WhatsAppTemplate,
} from '@/lib/api_whatsapp';

type Notifier = (n: {
  type: 'success' | 'error' | 'info';
  title: string; message?: string; duration?: number;
}) => void;

interface Props {
  addNotification?: Notifier;
  /** Dentro de la app WhatsApp: la app pinta el título; aquí solo las acciones. */
  embedded?: boolean;
}

const STATUS_LABEL: Record<BroadcastStatus, { label: string; cls: string; icon: JSX.Element }> = {
  pending:   { label: 'Pendiente',  cls: 'bg-muted text-muted-foreground',           icon: <Clock size={12} /> },
  running:   { label: 'En curso',   cls: 'bg-amber-500/15 text-amber-800 dark:text-amber-300',       icon: <Loader2 size={12} className="animate-spin" /> },
  completed: { label: 'Completada', cls: 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-300',   icon: <CheckCircle size={12} /> },
  failed:    { label: 'Fallida',    cls: 'bg-red-500/15 text-red-800 dark:text-red-300',           icon: <XCircle size={12} /> },
  cancelled: { label: 'Cancelada',  cls: 'bg-muted text-muted-foreground',         icon: <Ban size={12} /> },
};

export function WhatsAppBroadcastsPage({ addNotification, embedded = false }: Props) {
  const [rows, setRows] = useState<WhatsAppBroadcast[]>([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [pending, setPending] = useState<Record<number, 'start' | 'cancel' | 'delete'>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const out = await listBroadcasts({ limit: 50 });
      setRows(out.items);
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : e?.message || 'Error';
      addNotification?.({ type: 'error', title: 'No se cargaron los envíos', message: msg });
    } finally {
      setLoading(false);
    }
  }, [addNotification]);

  useEffect(() => { load(); }, [load]);

  // Poll every 8s while any broadcast is running — backend flips the status
  // + counters as each worker finishes. Stops once nothing is in flight.
  useEffect(() => {
    const anyRunning = rows.some((r) => r.status === 'running');
    if (!anyRunning) return;
    const t = setInterval(() => load(), 8000);
    return () => clearInterval(t);
  }, [rows, load]);

  const act = async (bc: WhatsAppBroadcast, op: 'start' | 'cancel' | 'delete') => {
    setPending((p) => ({ ...p, [bc.id]: op }));
    try {
      if (op === 'start')   await startBroadcast(bc.id);
      if (op === 'cancel')  await cancelBroadcast(bc.id);
      if (op === 'delete')  await deleteBroadcast(bc.id);
      addNotification?.({
        type: 'success',
        title:
          op === 'start' ? 'Envío en marcha'
          : op === 'cancel' ? 'Envío cancelado'
          : 'Envío eliminado',
      });
      await load();
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : e?.message || 'Error';
      addNotification?.({ type: 'error', title: 'No se pudo ejecutar', message: msg });
    } finally {
      setPending((p) => { const n = { ...p }; delete n[bc.id]; return n; });
    }
  };

  return (
    <div className="space-y-6">
      <div className={`flex flex-wrap items-center gap-3 ${embedded ? 'justify-end' : 'justify-between'}`}>
        {!embedded && (
        <div>
          <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Send className="wa-accent-text" />
            Envíos masivos
          </h2>
          <p className="text-muted-foreground text-sm mt-1">
            Envío masivo basado en una plantilla aprobada + regla de audiencia.
          </p>
        </div>
        )}
        <div className="flex flex-wrap gap-2">
          <button onClick={load} disabled={loading} className="inline-flex items-center justify-center rounded-lg border border-border bg-card hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))] disabled:opacity-50 px-3 py-2 text-sm flex items-center gap-2">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Actualizar
          </button>
          <button onClick={() => setShowWizard(true)} className="wa-btn inline-flex items-center justify-center rounded-lg font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))] focus-visible:ring-offset-2 focus-visible:ring-offset-background px-3 py-2 text-sm flex items-center gap-2">
            <Plus size={16} /> Nuevo envío
          </button>
        </div>
      </div>

      {loading && rows.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={28} className="animate-spin wa-accent-text" />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Send size={40} className="mx-auto mb-3 opacity-50" />
          <p>Todavía no hay envíos masivos.</p>
          <p className="text-sm mt-1">Crea uno para enviar una plantilla aprobada a una lista de contactos.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((bc) => {
            const st = STATUS_LABEL[bc.status];
            const busy = pending[bc.id];
            const pct = bc.total_recipients
              ? Math.round(((bc.sent_count + bc.failed_count) * 100) / bc.total_recipients)
              : 0;
            return (
              <div key={bc.id} className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-medium text-foreground truncate">{bc.name}</h4>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${st.cls}`}>
                        {st.icon} {st.label}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 flex gap-3 flex-wrap">
                      <span>Progreso: {broadcastProgressText(bc)}</span>
                      {bc.delivered_count > 0 && <span>Entregadas: {bc.delivered_count}</span>}
                      {bc.read_count > 0 && <span>Leídas: {bc.read_count}</span>}
                      {bc.started_at && <span>Inicio: {new Date(bc.started_at).toLocaleString('es-MX')}</span>}
                    </div>
                    {bc.error_message && (
                      <div className="text-xs text-amber-800 dark:text-amber-300 mt-1">{bc.error_message}</div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {(bc.status === 'pending' || bc.status === 'failed') && (
                      <button
                        onClick={() => act(bc, 'start')}
                        disabled={!!busy}
                        className="wa-btn inline-flex items-center justify-center rounded-lg font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))] focus-visible:ring-offset-2 focus-visible:ring-offset-background px-2.5 py-1.5 text-xs flex items-center gap-1"
                      >
                        {busy === 'start' ? <Loader2 size={14} className="animate-spin" /> : <PlayCircle size={14} />}
                        Iniciar
                      </button>
                    )}
                    {(bc.status === 'pending' || bc.status === 'running') && (
                      <button
                        onClick={() => act(bc, 'cancel')}
                        disabled={!!busy}
                        className="inline-flex items-center justify-center rounded-lg border border-border bg-card hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))] disabled:opacity-50 px-2.5 py-1.5 text-xs flex items-center gap-1"
                      >
                        {busy === 'cancel' ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />}
                        Cancelar
                      </button>
                    )}
                    {(bc.status !== 'running') && (
                      <button
                        onClick={() => act(bc, 'delete')}
                        disabled={!!busy}
                        className="inline-flex items-center justify-center rounded-lg border border-border bg-card hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))] disabled:opacity-50 px-2.5 py-1.5 text-xs text-destructive flex items-center gap-1"
                      >
                        {busy === 'delete' ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
                        Eliminar
                      </button>
                    )}
                  </div>
                </div>
                {bc.total_recipients > 0 && (
                  <div className="mt-3 h-1.5 bg-muted rounded overflow-hidden">
                    <div
                      className={`h-full ${bc.status === 'failed' ? 'bg-red-500/60' : 'bg-emerald-500/70'}`}
                      style={{ width: `${Math.min(100, pct)}%` }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showWizard && (
        <NewBroadcastWizard
          onClose={() => setShowWizard(false)}
          onCreated={async () => { setShowWizard(false); await load(); }}
          notify={addNotification}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Wizard
// ═══════════════════════════════════════════════════════════════

function NewBroadcastWizard({
  onClose, onCreated, notify,
}: {
  onClose: () => void;
  onCreated: () => void | Promise<void>;
  notify?: Notifier;
}) {
  const [name, setName] = useState('');
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [templateId, setTemplateId] = useState<number | ''>('');
  const [loadingTpl, setLoadingTpl] = useState(true);

  const [opted, setOpted] = useState<'any' | 'true' | 'false'>('any');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [excludeInput, setExcludeInput] = useState('');
  const [excludeTags, setExcludeTags] = useState<string[]>([]);
  const [phonesText, setPhonesText] = useState('');

  const [variablesText, setVariablesText] = useState(''); // comma-separated
  const [saving, setSaving] = useState(false);

  const [preview, setPreview] = useState<{ count: number; busy: boolean }>({ count: 0, busy: false });

  useEffect(() => {
    (async () => {
      setLoadingTpl(true);
      try {
        const out = await listTemplates({ status: 'approved', limit: 100 });
        setTemplates(out.items);
      } finally {
        setLoadingTpl(false);
      }
    })();
  }, []);

  const buildAudience = useCallback((): Record<string, unknown> => {
    const a: Record<string, unknown> = {};
    if (opted !== 'any') a.opted_in = opted === 'true';
    if (tags.length) a.tags = tags;
    if (excludeTags.length) a.exclude_tags = excludeTags;
    const phones = phonesText
      .split(/[\s,;\n]+/)
      .map((x) => x.trim())
      .filter(Boolean);
    if (phones.length) a.phone_numbers = phones;
    return a;
  }, [opted, tags, excludeTags, phonesText]);

  const runPreview = async () => {
    if (!templateId) return;
    setPreview((p) => ({ ...p, busy: true }));
    try {
      // Preview path: create the broadcast as a draft, fetch its audience,
      // then leave the row in pending for the user to start or delete.
      // Backend does not expose a standalone audience-resolve endpoint.
      const draft = await createBroadcast({
        name: `__preview__ ${name || '(sin nombre)'}`,
        template_id: Number(templateId),
        audience: buildAudience(),
      });
      const pre = await previewBroadcastAudience(draft.id);
      // Always clean up the preview row so the list doesn't fill with drafts.
      try { await deleteBroadcast(draft.id); } catch { /* ignore */ }
      setPreview({ count: pre.count, busy: false });
    } catch (e: any) {
      setPreview({ count: 0, busy: false });
      notify?.({
        type: 'error',
        title: 'Preview falló',
        message: e instanceof ApiError ? e.message : e?.message || 'Error',
      });
    }
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!templateId) {
      notify?.({ type: 'error', title: 'Selecciona un template' });
      return;
    }
    setSaving(true);
    try {
      const variables = variablesText
        .split(/\s*,\s*/)
        .map((x) => x.trim())
        .filter(Boolean);
      await createBroadcast({
        name: name.trim() || 'Envío masivo',
        template_id: Number(templateId),
        audience: buildAudience(),
        variables_default: variables.length ? { variables } : {},
      });
      notify?.({ type: 'success', title: 'Envío creado' });
      await onCreated();
    } catch (e: any) {
      notify?.({
        type: 'error',
        title: 'No se pudo crear',
        message: e instanceof ApiError ? e.message : e?.message || 'Error',
      });
    } finally {
      setSaving(false);
    }
  };

  const addTag = (input: string, setter: (arr: string[]) => void, current: string[], clear: () => void) => {
    const t = input.trim();
    if (!t) return;
    setter(Array.from(new Set([...current, t])));
    clear();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card text-card-foreground rounded-xl border border-border max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-xl font-semibold text-foreground mb-4">Nuevo envío</h3>

          <form onSubmit={save} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Nombre</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Promoción marzo"
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:border-emerald-500 focus:outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Template (solo aprobadas)</label>
              {loadingTpl ? (
                <div className="py-2"><Loader2 className="inline animate-spin wa-accent-text" /></div>
              ) : templates.length === 0 ? (
                <div className="text-sm text-amber-800 dark:text-amber-300">
                  No hay plantillas aprobadas. Crea y envía una a revisión en la pestaña Templates.
                </div>
              ) : (
                <select
                  value={templateId}
                  onChange={(e) => setTemplateId(e.target.value ? Number(e.target.value) : '')}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:border-emerald-500 focus:outline-none"
                  required
                >
                  <option value="">— Seleccionar —</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>{t.name} ({t.language})</option>
                  ))}
                </select>
              )}
            </div>

            <div className="border border-border rounded-lg p-3 space-y-3">
              <div className="text-sm font-medium text-foreground">Audiencia</div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Permiso de mensajes</label>
                  <select
                    value={opted}
                    onChange={(e) => setOpted(e.target.value as any)}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm"
                  >
                    <option value="any">Cualquiera</option>
                    <option value="true">Solo quienes aceptaron promociones (marketing)</option>
                    <option value="false">Solo quienes no aceptaron (avisos de servicio)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Con todas estas etiquetas</label>
                  <div className="flex gap-2">
                    <input
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(tagInput, setTags, tags, () => setTagInput('')); } }}
                      placeholder="vip"
                      className="flex-1 px-3 py-1.5 bg-background border border-border rounded-lg text-foreground text-sm focus:border-emerald-500 focus:outline-none"
                    />
                    <button type="button" onClick={() => addTag(tagInput, setTags, tags, () => setTagInput(''))} className="inline-flex items-center justify-center rounded-lg border border-border bg-card hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))] disabled:opacity-50 px-2 py-1 text-xs">+</button>
                  </div>
                  <div className="flex gap-1 flex-wrap mt-2">
                    {tags.map((t) => (
                      <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 text-xs text-emerald-800 dark:text-emerald-300">
                        {t}
                        <button type="button" onClick={() => setTags(tags.filter((x) => x !== t))} className="text-emerald-800 dark:text-emerald-200 hover:text-foreground">×</button>
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-muted-foreground mb-1">Sin estas etiquetas</label>
                  <div className="flex gap-2">
                    <input
                      value={excludeInput}
                      onChange={(e) => setExcludeInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(excludeInput, setExcludeTags, excludeTags, () => setExcludeInput('')); } }}
                      placeholder="unsub"
                      className="flex-1 px-3 py-1.5 bg-background border border-border rounded-lg text-foreground text-sm focus:border-emerald-500 focus:outline-none"
                    />
                    <button type="button" onClick={() => addTag(excludeInput, setExcludeTags, excludeTags, () => setExcludeInput(''))} className="inline-flex items-center justify-center rounded-lg border border-border bg-card hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))] disabled:opacity-50 px-2 py-1 text-xs">+</button>
                  </div>
                  <div className="flex gap-1 flex-wrap mt-2">
                    {excludeTags.map((t) => (
                      <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/15 text-xs text-red-800 dark:text-red-300">
                        {t}
                        <button type="button" onClick={() => setExcludeTags(excludeTags.filter((x) => x !== t))} className="text-red-800 dark:text-red-200 hover:text-foreground">×</button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs text-muted-foreground mb-1">
                  Lista explícita de teléfonos (opcional — hace override de todo lo anterior)
                </label>
                <textarea
                  value={phonesText}
                  onChange={(e) => setPhonesText(e.target.value)}
                  placeholder="+5215511112222, +5215511113333"
                  rows={2}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:border-emerald-500 focus:outline-none font-mono text-sm"
                />
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={runPreview}
                  disabled={!templateId || preview.busy}
                  className="inline-flex items-center justify-center rounded-lg border border-border bg-card hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))] disabled:opacity-50 px-2.5 py-1.5 text-xs flex items-center gap-2"
                >
                  {preview.busy ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
                  Preview
                </button>
                <span className="text-sm text-foreground">
                  {preview.count > 0 ? (
                    <><Users size={12} className="inline mr-1" /> {preview.count} contacto{preview.count === 1 ? '' : 's'}</>
                  ) : (
                    <span className="text-muted-foreground">Pulsa preview para estimar destinatarios.</span>
                  )}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Variables por defecto (separadas por coma — para {'{{1}}'}, {'{{2}}'}, …)
              </label>
              <input
                type="text"
                value={variablesText}
                onChange={(e) => setVariablesText(e.target.value)}
                placeholder="Calaita, Primavera"
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:border-emerald-500 focus:outline-none"
              />
            </div>

            <div className="flex justify-end gap-3 pt-3 border-t border-border">
              <button type="button" onClick={onClose} className="inline-flex items-center justify-center rounded-lg border border-border bg-card hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))] disabled:opacity-50 px-3 py-2 text-sm">Cancelar</button>
              <button type="submit" disabled={saving} className="wa-btn inline-flex items-center justify-center rounded-lg font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))] focus-visible:ring-offset-2 focus-visible:ring-offset-background px-3 py-2 text-sm flex items-center gap-2">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <ChevronRight size={16} />}
                Crear envío
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default WhatsAppBroadcastsPage;
